/**
 * Country list - Figma `6377:87534` (and `6377:87602`), the visa destination picker.
 *
 * A grid of country cards on the left and a detail panel on the right. Picking a country
 * fills the panel: flag, name, the visa notes, and the document links for that country.
 * The selected card is filled blue, as in the design.
 *
 * **`shared.country-item` gained `links`.** Each country in the design carries its own
 * document links - required documents, e-visa forms - and the component had nowhere for
 * them.
 *
 * The cards are radio buttons in a radio group rather than clickable divs: this is a
 * single choice out of many, which is what a radio group is.
 *
 * **Measured against 6377:87536 and 6377:87604 on 2026-09-26.** Square cards - capital
 * 14px, flag in its grey ring halfway down, name h5-Medium - a detail panel as tall as the
 * grid, and **nothing picked until the visitor picks**: the panel then shows the file's
 * dotted globe and the section's `body` as its sentence. Under the grid, the Sherpa card:
 * the CTA's label as a chip, `ctaHeading` beside the button.
 *
 * **`branches-teaser` was merged into this on 2026-09-04.** Both were a list of countries
 * that swaps what is shown beside it when one is picked, from the same
 * `shared.country-item`; `layout` chooses which of the two the file draws.
 *
 * - `grid` (the default) is the visa page: cards beside a detail panel (`6377:87534`).
 * - `split` is the company page: a photograph beside the list, where picking a country
 *   swaps the photograph and the paragraph (`6377:87719`). The selected name is blue and
 *   underlined and the rest grey, exactly as the design draws it.
 */
import { useState } from 'react';
import { CtaLink, Eyebrow, SectionShell } from '../primitives';
import { Image, SIZES } from '../../components/ui/Image';
import { Icon } from '../../components/ui/Icon';
import { mediaUrl } from '../../lib/config';
import { useSite } from '../../lib/site-context';
import type { CountryItem, CountryListSection } from '../../types/sections';
import { AppLink } from '../../components/ui/AppLink';
import worldDots from '../../assets/world-dots.svg';

function Flag({ country, size }: { country: CountryItem; size: number }) {
  if (!country.flag) return null;
  return (
    <img
      src={mediaUrl(country.flag.url)}
      alt=""
      loading="lazy"
      style={{ width: size, height: size }}
      className="shrink-0 rounded-full object-cover"
    />
  );
}

/** A flag in the grey ring the file draws round every one (6377:87544). */
function FlagRing({ country, size, ring }: { country: CountryItem; size: number; ring: string }) {
  if (!country.flag) return null;
  return (
    <span className={`inline-flex shrink-0 rounded-full ${ring}`}>
      <Flag country={country} size={size} />
    </span>
  );
}

/**
 * One country, 6377:87541: a square with the capital at the top, the name at the foot and
 * the flag halfway between - the middle row takes whatever height is left, so a name that
 * wraps to three lines ("Stany Zjednoczone Ameryki") lifts the flag rather than covering it.
 */
function CountryCard({ country, active, onPick }: { country: CountryItem; active: boolean; onPick: () => void }) {
  const { t } = useSite();
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onPick}
      className={`grid aspect-square grid-rows-[auto_1fr_auto] rounded-l p-4 text-left transition-colors sm:p-7 ${
        active ? 'bg-blue-500 text-white' : 'bg-surface text-ink-strong hover:bg-surface-muted'
      }`}
    >
      <span className={`text-body-sm ${active ? 'text-white' : 'text-ink'}`}>
        {country.capital ? `${t('country.capital')} | ${country.capital}` : null}
      </span>
      <span className="self-center"><FlagRing country={country} size={40} ring="bg-grey-500 p-1" /></span>
      <span className="text-h5 font-medium">{country.name}</span>
    </button>
  );
}

/** The frame both panels share, 6377:87660: a grey rule, 12 in, and a white card inside it. */
function PanelFrame({ children, className }: { children: React.ReactNode; className: string }) {
  return (
    <div className="rounded-m border border-grey-300 p-3 lg:aspect-square">
      <div className={`h-full rounded-s bg-surface p-8 ${className}`}>{children}</div>
    </div>
  );
}

/**
 * The picked country, 6377:87592: flag and name at h2-Medium, the notes, the documents.
 * The notes are one line each, the first - the capital, as every entry starts - in
 * SemiBold, and set narrow, 212 as drawn.
 */
function CountryPanel({ country }: { country: CountryItem }) {
  const lines = (country.body ?? '').split('\n').map((line) => line.trim()).filter(Boolean);
  return (
    <PanelFrame className="flex flex-col justify-between gap-10">
      <div className="flex items-center gap-9">
        <FlagRing country={country} size={50} ring="border border-grey-300 bg-grey-100 p-2" />
        <h3 className="text-h3 font-medium text-ink-strong lg:text-h2">{country.name}</h3>
      </div>

      {lines.length ? (
        <div className="flex max-w-[212px] flex-col gap-5 text-body-sm text-ink-strong">
          {lines.map((line, i) => <p key={i} className={i === 0 ? 'font-semibold' : undefined}>{line}</p>)}
        </div>
      ) : null}

      {country.links?.length ? (
        <ul className="flex flex-col gap-2">
          {country.links.map((link, i) => (
            <li key={i}>
              <AppLink
                to={link.url}
                className="inline-flex items-center gap-4 text-body-sm text-brand-500 underline decoration-from-font hover:text-brand-600"
              >
                <Icon name="file-download" size={24} />
                {link.label}
              </AppLink>
            </li>
          ))}
        </ul>
      ) : null}
    </PanelFrame>
  );
}

/**
 * Nothing picked yet, 6377:87660: the dotted globe, a round badge with a flag on it where
 * the globe meets the sentence, and the sentence - the section's `body`, so a market words
 * its own. The badge's centre sits 79% down the globe, where the file puts it.
 *
 * The globe is a bundled file, not a CMS picture, so it is a plain `img`: `Image` builds
 * its `srcset` from Strapi's formats, and a vector has none to choose between.
 */
function EmptyPanel({ text }: { text?: string }) {
  return (
    <PanelFrame className="flex flex-col items-center justify-center gap-2 text-center">
      <div className="relative w-full max-w-[587px]">
        <img src={worldDots} alt="" width={587} height={262} loading="lazy" className="h-auto w-full" />
        <span className="absolute left-1/2 top-[79%] grid h-[94px] w-[94px] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-ink-strong/10 bg-white/10 text-blue-500 shadow-lg backdrop-blur-[10px]">
          <Icon name="flag-check" size={26} />
        </span>
      </div>
      {text ? <p className="max-w-[419px] whitespace-pre-line text-body-md text-ink-muted">{text}</p> : null}
    </PanelFrame>
  );
}

export default function CountryList({ data }: { data: CountryListSection }) {
  const { t } = useSite();
  const countries = data.countries ?? [];
  // Nothing is picked until the visitor picks (6377:87604); the split layout still opens on
  // its first country, whose photograph and paragraph are what it shows.
  const [selected, setSelected] = useState<number | null>(null);
  const active = selected === null ? undefined : countries[selected];

  if (!countries.length) return null;

  // The company page's treatment: one photograph, the country names as a selector, and
  // the selected country's paragraph under them.
  if (data.layout === 'split') {
    const current = selected ?? 0;
    const shown = countries[current];
    const image = shown?.image ?? data.media?.image;
    const body = shown?.body ?? data.body;

    return (
      <SectionShell settings={data.settings} name="country-list">
        {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}

        <div className="grid gap-4 lg:grid-cols-2">
          <figure className="m-0 min-h-[420px] overflow-hidden rounded-l bg-surface-sunken">
            {image ? (
              <Image
                media={image}
                alt={image.alternativeText ?? shown?.name ?? ''}
                sizes={SIZES.half}
                className="h-full w-full object-cover"
              />
            ) : null}
          </figure>

          <div className="flex flex-col justify-between gap-10 lg:py-2">
            <h2 className="text-h4 text-ink">{data.heading}</h2>

            <div role="tablist" aria-label={data.heading} className="flex flex-col items-start gap-3">
              {countries.map((country, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === current}
                  onClick={() => setSelected(i)}
                  className={`text-h5 transition ${
                    i === current
                      ? 'text-brand-500 underline underline-offset-8'
                      : 'text-ink-faint hover:text-ink-muted'
                  }`}
                >
                  {country.name}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-6">
              {body ? <p className="max-w-lg whitespace-pre-line text-body-sm text-ink-muted">{body}</p> : null}
              {data.cta ? <CtaLink value={data.cta} /> : null}
            </div>
          </div>
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell settings={data.settings} name="country-list">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
      {/* h3's 34px over four fifths of the page, the designer's measure (2026-09-26). */}
      {data.heading ? <h2 className="text-h3 text-ink lg:max-w-[80%]">{data.heading}</h2> : null}
      <p className="text-body-md text-ink-strong">{t('country.select')}</p>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* 6377:87540: three across on a 16px gutter, every card a square. */}
        <div role="radiogroup" aria-label={t('country.select')} className="grid grid-cols-2 gap-4 self-start sm:grid-cols-3">
          {countries.map((country, i) => (
            <CountryCard key={i} country={country} active={i === selected} onPick={() => setSelected(i)} />
          ))}
        </div>

        {active ? <CountryPanel country={active} /> : <EmptyPanel text={data.body} />}
      </div>

      {/* 6377:87593 - the card under the grid pointing at the travel-requirements tool, at
          the width of the grid column: the tool's name as a chip, a line, and the button. */}
      {data.cta ? (
        <div className="lg:w-[calc(50%-0.5rem)]">
          <div className="flex flex-col gap-6 rounded-l border border-grey-300 bg-surface p-7">
            <span className="inline-flex w-fit rounded-full border border-ink/10 bg-surface px-4 py-1.5 text-body text-ink">
              {data.cta.label}
            </span>
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              {/* The line was the translated "Sprawdź wymogi podróży" before it was a field,
                  so an entry saved before then still reads the same. */}
              <p className="text-h4 font-medium text-ink-strong">{data.ctaHeading || t('country.requirements')}</p>
              <CtaLink value={data.cta} />
            </div>
          </div>
        </div>
      ) : null}
    </SectionShell>
  );
}
